import React from "react";
import {
  Container,
  Card,
  Text,
  Button,
  Group,
  Stack,
  ThemeIcon,
  Badge,
  Paper,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import {
  IconShieldX,
  IconArrowLeft,
  IconHome,
  IconLock,
  IconExclamationMark,
  IconUserX,
  IconAlertTriangle,
  IconShield,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import "./styles.scss";

const Unauthorized: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    window.location.href = "/d";
  };

  return (
    <div className="unauthorized-container">
      <Container
        size="lg"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          shadow="xl"
          padding="xl"
          radius="xl"
          withBorder
          style={{
            background:
              "linear-gradient(135deg, #fff5f5 0%, #fef2f2 50%, #fff1f2 100%)",
            border: "1px solid #fca5a5",
            maxWidth: 800,
            width: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative background elements */}
          <div
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              background: "rgba(239, 68, 68, 0.1)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 100,
              height: 100,
              background: "rgba(239, 68, 68, 0.05)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: -20,
              width: 60,
              height: 60,
              background: "rgba(251, 113, 133, 0.1)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <Stack gap="xl" align="center" ta="center">
              {/* Main Icon with Animation */}
              <div style={{ position: "relative" }}>
                <ThemeIcon
                  size={100}
                  radius="xl"
                  variant="gradient"
                  gradient={{ from: "red", to: "pink", deg: 45 }}
                  style={{
                    boxShadow: "0 8px 32px rgba(239, 68, 68, 0.4)",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <IconShieldX size={50} />
                </ThemeIcon>

                {/* Warning indicator */}
                <div
                  style={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    background: "linear-gradient(45deg, #fbbf24, #f59e0b)",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(251, 191, 36, 0.5)",
                    animation: "bounce 1s infinite",
                  }}
                >
                  <IconExclamationMark size={18} color="white" />
                </div>
              </div>

              {/* Header */}
              <Stack gap="sm" align="center">
                <Group gap={8} align="center">
                  <Badge
                    variant="gradient"
                    gradient={{ from: "red", to: "pink", deg: 45 }}
                    size="lg"
                    style={{ fontSize: 12 }}
                  >
                    ACCESS DENIED
                  </Badge>
                </Group>

                <Text fw={700} size="xl" c="red" style={{ fontSize: 28 }}>
                  Unauthorized Access
                </Text>

                <Text
                  size="md"
                  c="dimmed"
                  style={{ lineHeight: 1.6, maxWidth: 400 }}
                >
                  You don't have the necessary permissions to access this page.
                  Please contact your administrator if you believe this is an
                  error.
                </Text>
              </Stack>

              {/* Error Details */}
              <Paper
                p="md"
                radius="md"
                style={{
                  background: "rgba(255, 255, 255, 0.8)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                  width: "100%",
                }}
              >
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  <Group gap={8} justify="center">
                    <ThemeIcon size="sm" color="red" variant="light">
                      <IconLock size={14} />
                    </ThemeIcon>
                    <Text size="xs" fw={500} c="dark">
                      Protected Resource
                    </Text>
                  </Group>

                  <Group gap={8} justify="center">
                    <ThemeIcon size="sm" color="orange" variant="light">
                      <IconUserX size={14} />
                    </ThemeIcon>
                    <Text size="xs" fw={500} c="dark">
                      Insufficient Privileges
                    </Text>
                  </Group>

                  <Group gap={8} justify="center">
                    <ThemeIcon size="sm" color="violet" variant="light">
                      <IconShield size={14} />
                    </ThemeIcon>
                    <Text size="xs" fw={500} c="dark">
                      Admin Required
                    </Text>
                  </Group>
                </SimpleGrid>
              </Paper>

              <Divider w="100%" color="rgba(239, 68, 68, 0.2)" />

              {/* Action Buttons */}
              <Group gap="md" justify="center" w="100%">
                <Button
                  variant="outline"
                  color="red"
                  size="md"
                  leftSection={<IconHome size={18} />}
                  onClick={handleGoHome}
                  style={{
                    borderColor: "#ef4444",
                    color: "#ef4444",
                  }}
                >
                  Dashboard
                </Button>
              </Group>

              {/* Help Text */}
              <Paper
                p="sm"
                radius="md"
                style={{
                  background: "rgba(59, 130, 246, 0.1)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  width: "100%",
                }}
              >
                <Group gap={8} justify="center" align="center">
                  <ThemeIcon size="xs" color="blue" variant="light">
                    <IconAlertTriangle size={12} />
                  </ThemeIcon>
                  <Text size="xs" c="blue" fw={500} ta="center">
                    Need access? Contact your system administrator for
                    permission requests
                  </Text>
                </Group>
              </Paper>
            </Stack>
          </div>
        </Card>
      </Container>
    </div>
  );
};

export default Unauthorized;
